import { autoinject, transient, computedFrom } from "aurelia-framework";
import { QueriesClientService } from "../../../../services/queries/queries-client.services";
import { Router } from "aurelia-router";
import { Reflection } from "../../../../utilities/reflection";
import { I18N } from "aurelia-i18n";
import { ActiveMenuItemsService } from "../../../../services/basics/active-menu-items.service";
import { WorkflowForEntity } from "../../../components/workflow-for-entity/workflow-for-entity";
import { ClipboardCopyService } from "../../../../utilities/clipboard-copy-service";
import { ManageVesselService } from "../../../../services/vessels/manage-vessel.service";
import { VesselDetailModel, RatingsSummaryModel, ISubtaskEntity } from "../../../../models/vessels/vessel.models";
import { EmailHistory } from "../../../pages/modules/common-steps/email-history/email-history";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../constants/authentication/claims";
import { RightShipOnboardingService } from "../../../../services/rightship-onboarding/rightship-onboarding.service";
import { PreviousFormCommentModel } from "../../../../models/onboarding/rightship-onboarding.models";
import { RightShipQiRouterConstants } from "../../../../configuration/rightshipqi-router.constants"
import { RightShipWorkflowStateEnum } from "../../../../models/workflow/rightship.workflow.models";
import { Guid } from "../../../../utilities/guid";
import { OnboardingRequestsModel } from "./onboarding-requests-model";
import * as Enumerable from "linq";
import { RightShipOnboardingQuestionCommentService } from "../../../../services/rightship-onboarding/rightship-onboarding-question-comment.service";

@autoinject()
@transient()
export class VetTaskOnboardingCustomContent {

    private _model: { onboardingRequestId: number, vesselApplicationFormId: number };
    public isScrolled: boolean = false;
    public vesselHeaderElem: HTMLElement;
    public onboardingRequests: OnboardingRequestsModel[] = [];
    public vesselApplicationFormId: number;
    public pluginEntityTypeId: number;
    public vetId: number;
    public vesselId: number;
    public workflowForEntity: WorkflowForEntity;
    public formTitle: string = "";
    public defaultOnboardingRequestId: number;
    public vessel: VesselDetailModel;
    public ratingModel: RatingsSummaryModel;
    public previousFormComment: PreviousFormCommentModel;
    public tabs: { title: string, id: number }[];
    public currentTab: { title: string, id: number };
    public titleWrapElem: HTMLElement;
    private _interactionObserver: IntersectionObserver;

    constructor(private queryService: QueriesClientService, private router: Router, private manageVesselService: ManageVesselService,
        private i18N: I18N, private activeMenuItemsService: ActiveMenuItemsService, private clipboardCopyService: ClipboardCopyService,
        protected emailHistory: EmailHistory, private userClaimsService: UserClaimsService,
        private rightShipOnboardingService: RightShipOnboardingService, private questionCommentsService: RightShipOnboardingQuestionCommentService) {

        this.tabs = [{ title: "Form Meta", id: 1 }]
            .concat(this.userClaimsService.hasPermission(Claims.CanViewFormEmailHistory) ? [
                { title: "Email History", id: 2 }
            ] : []);
        this.currentTab = this.tabs[0];
    }

    activate(model: { onboardingRequestId: number, vesselApplicationFormId: number }) {
        this._model = model;
        this.vesselApplicationFormId = model.vesselApplicationFormId;
        this.activeMenuItemsService.setActiveFromMenuItem(this.i18N.tr('tasks-text'));
    }

    async attached() {

        //Get all requests
        const getAllRequests = async () => {
            if (this._model && this._model.vesselApplicationFormId) {
                const x = await this.queryService.getData('odata/VetTaskOnboardingRequests?$filter=(vesselApplicationFormId eq ' + this._model.vesselApplicationFormId +
                    ')&$orderby=createdDate desc, onboardingRequestId desc&$select=onboardingRequestId, isPrefilledOnboardingRequest, name, vetId, vesselId, createdDate, entityTypeId, validityExpiryDate')

                this.onboardingRequests = this.groupOnboardingRequestData(x.value);
                this.pluginEntityTypeId = this.onboardingRequests[0].entityTypeId;
                this.vesselId = this.onboardingRequests[0].vesselId;
                this.formTitle = this.onboardingRequests[0].name;
                this.vetId = this.onboardingRequests[0].vetIds[0];
                if (this._model.onboardingRequestId) {
                    this.defaultOnboardingRequestId = this._model.onboardingRequestId;
                }
                else {
                    this.defaultOnboardingRequestId = this.onboardingRequests.filter(c => !c.isPrefilledOnboardingRequest)[0].onboardingRequestId;
                }

                this.defaultOnboardingRequestId = this.onboardingRequests.filter(x => x.onboardingRequestId == this.defaultOnboardingRequestId)[0].onboardingRequestId;

                if (this.vesselId) {
                    const res = await this.manageVesselService.getVessel({
                        vesselId: this.vesselId,
                        imo: null,
                        bKey: null
                    });
                    this.ratingModel = await this.manageVesselService.getRatingsModel(this.vesselId);
                    this.vessel = res.vessel;
                }

                this.previousFormComment = await this.rightShipOnboardingService.getPreviousFormComment(this._model.vesselApplicationFormId);
            }
        }

        if (this._model && this._model.onboardingRequestId && (!this._model.vesselApplicationFormId)) {
            const x = await this.queryService.getData('odata/VetTaskOnboardingRequests?$filter=((onboardingRequestId eq ' + this._model.onboardingRequestId +
                '))&$select=vesselApplicationFormId,entityTypeId');
                this._model.vesselApplicationFormId = x.value[0].vesselApplicationFormId;
            await getAllRequests();
        }
        else {
            await getAllRequests();
        }

        //Observer to see when the position: sticky is set on the text-wrap element
        this._interactionObserver  = new IntersectionObserver( 
        ([e]) => {
            e.target.classList.toggle('is-sticky', e.intersectionRatio < 1);
            }, {threshold: [1]}
        );

        this._interactionObserver.observe(this.titleWrapElem)
    }

    detached() {
        //Disconner observer
        this._interactionObserver?.disconnect();
    }

    async canDeactivate() {
        try {
            const res = await this.questionCommentsService.canDeactivate();
            return res;
        }
        catch (e) {
            console.error("Failed to deactivate the form page", e);
            return false;
        }
    }

    @computedFrom("vesselApplicationFormId")
    get emailHistoryModel(): { item: ISubtaskEntity } {
        return {
            item: {
                id: this.vesselApplicationFormId,
                entityType: EntityTypes.VesselApplicationForms,
                identifier: Guid.generateGuid()
            }
        };
    }

    @computedFrom(Reflection.getPropertyPath<VetTaskOnboardingCustomContent>(v => v.onboardingRequests))
    public get vetUrls(): string[] {
        const vetUrls: any[] = [];
        const onboardingRequest = this.onboardingRequests.find(x => x.onboardingRequestId === this.defaultOnboardingRequestId);
        if (onboardingRequest != null || onboardingRequest != undefined) {
            onboardingRequest.vetIds.forEach(vetId => {
                if (vetId) {
                    const vetUrl = this.router.generate(RightShipQiRouterConstants.RouteNames.Vet, { id: vetId });
                    vetUrls.push({ vetUrl, vetId });
                }
            });
        }
        return vetUrls;
    }

    @computedFrom("workflowForEntity", "workflowForEntity.workflowState", "workflowForEntity.workflowState.state")
    public get showReopen() {

        if (!this.workflowForEntity || !this.workflowForEntity.workflowState || !this.workflowForEntity.workflowState.workflowAssignments ||
            this.workflowForEntity.workflowState.workflowAssignments.length == 0)
            return false;

        return (this.workflowForEntity.workflowState.state == RightShipWorkflowStateEnum.Accepted || this.workflowForEntity.workflowState.state == RightShipWorkflowStateEnum.Unacceptable);
    }

    @computedFrom("workflowForEntity", "workflowForEntity.workflowState", "workflowForEntity.workflowState.state")
    public get showCancel() {
        if (!this.workflowForEntity || !this.workflowForEntity.workflowState)
            return false;

        return !(this.workflowForEntity.workflowState.state == RightShipWorkflowStateEnum.Unacceptable || this.workflowForEntity.workflowState.state == RightShipWorkflowStateEnum.Cancelled)
    }

    @computedFrom("workflowForEntity", "workflowForEntity.workflowState", "workflowForEntity.workflowState.state")
    public get showValidity() {
        if (!this.workflowForEntity || !this.workflowForEntity.workflowState)
            return false;
        return this.workflowForEntity.workflowState.state == RightShipWorkflowStateEnum.Accepted && this.onboardingRequests[0].entityTypeId != EntityTypes.Moc;
    }

    @computedFrom("workflowForEntity", "workflowForEntity.workflowState", "workflowForEntity.workflowState.state")
    public get cancelButtonText() {

        if (this.workflowForEntity && this.workflowForEntity.workflowState && this.workflowForEntity.workflowState.state == RightShipWorkflowStateEnum.Accepted) {
            return 'Invalidate';
        }
        else {
            return 'Cancel Workflow';
        }
    }


    @computedFrom(Reflection.getPropertyPath<VetTaskOnboardingCustomContent>(v => v.router.currentInstruction))
    public get previousInstructionTitle(): string {
        const prevInstruction = this.router.currentInstruction.previousInstruction;
        return prevInstruction
            ? prevInstruction.config.title
            : this.i18N.tr('back-text');
    }

    @computedFrom(Reflection.getPropertyPath<VetTaskOnboardingCustomContent>(v => v.vessel.vesselId))
    public get vesselUrl() {
        if(this.vessel){
            return this.router.generate(RightShipQiRouterConstants.RouteNames.VesselDetails, {
                id: this.vessel.vesselId,
            });
        }

        return "";
    }

    public navigateBack() {
        this.router.navigateBack();
    }

    copy(str: string) {
        this.clipboardCopyService.copy(str);
    }

    groupOnboardingRequestData(onboardingRequests: any[]) {
        const result = Enumerable.from(onboardingRequests)
            .groupBy(
                g => g.onboardingRequestId,
                element => element,
                (key, items) => (
                    {
                        onboardingRequestId: key,
                        createdDate: items.firstOrDefault().createdDate,
                        isPrefilledOnboardingRequest: items.firstOrDefault().isPrefilledOnboardingRequest,
                        entityTypeId: items.firstOrDefault().entityTypeId,
                        vesselId: items.firstOrDefault().vesselId,
                        name: items.firstOrDefault().name,
                        validityExpiryDate: items.firstOrDefault().validityExpiryDate,
                        vetIds: items.select(x => x.vetId).toArray()
                    }
                )).toArray();

        return result;
    }
}